import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Heart } from "lucide-react";
import Seo from "@/components/Seo";
import { GoldBtn, OutlineBtn } from "@/components/Bits";
import { QUOTE } from "@/config/content";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [tx, setTx] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
        attempts.current += 1;
        if (attempts.current >= 8) { setState("pending"); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        attempts.current += 1;
        if (attempts.current >= 8) { setState("error"); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const isTicket = tx?.kind === "ticket";

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-32">
      <Seo title="Thank You" description="Your contribution to EHSIMFFA has been received." path="/payment/success" />
      <div className="w-full max-w-xl text-center" data-testid="payment-success">
        {state === "checking" && (
          <div>
            <Loader2 className="mx-auto h-14 w-14 animate-spin text-gold" />
            <p className="mt-6 font-serif text-2xl text-ivory">Confirming your payment…</p>
          </div>
        )}
        {state === "paid" && (
          <div>
            <CheckCircle2 className="mx-auto h-16 w-16 text-gold" />
            <h1 className="mt-6 font-serif text-5xl font-light text-ivory">Thank you.</h1>
            <p className="mt-4 text-lg text-ivory2">
              {isTicket
                ? `Your ${tx?.amount ? `$${tx.amount.toFixed(2)} ` : ""}Gala ticket purchase is confirmed. We've emailed your confirmation — please present it at the door.`
                : `Your ${tx?.frequency === "monthly" ? "monthly " : ""}gift${tx?.amount ? ` of $${tx.amount.toFixed(2)}` : ""} has been received. A receipt is on its way to your email.`}
            </p>
            <blockquote className="mt-10 border-l-2 border-gold pl-6 text-left font-serif text-xl italic text-ivory2">
              "{QUOTE.text}"
              <footer className="mt-2 text-gold not-italic text-base">— {QUOTE.attribution}, {QUOTE.year}</footer>
            </blockquote>
            <div className="mt-10"><GoldBtn to="/" testid="success-home">Return Home</GoldBtn></div>
          </div>
        )}
        {state === "pending" && (
          <div>
            <Loader2 className="mx-auto h-14 w-14 animate-spin text-gold" />
            <h1 className="mt-6 font-serif text-4xl font-light text-ivory">Almost there…</h1>
            <p className="mt-4 text-ivory2">Your payment is processing. You'll receive an email confirmation shortly. Thank you for your generosity.</p>
            <div className="mt-8"><OutlineBtn to="/" testid="pending-home">Return Home</OutlineBtn></div>
          </div>
        )}
        {state === "error" && (
          <div>
            <XCircle className="mx-auto h-14 w-14 text-ethred" />
            <h1 className="mt-6 font-serif text-4xl font-light text-ivory">We couldn't confirm this payment.</h1>
            <p className="mt-4 text-ivory2">If you were charged, you will still receive a confirmation email. Please contact us if you have any questions.</p>
            <div className="mt-8 flex justify-center gap-4">
              <OutlineBtn to="/contact" testid="error-contact">Contact Us</OutlineBtn>
              <GoldBtn to="/" testid="error-home"><Heart className="h-5 w-5" /> Return Home</GoldBtn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
